import type {
  AdminOverview,
  EntitlementOverview,
  GrantRecord,
  Restriction,
  SubscriptionOverview,
} from '@fruuit/shared'
import { api } from '../../api/client'
import { useApiMutation, useApiQuery } from '../../api/hooks'

/** Admin data APIs (P-ADM-010, P-CAN-005) and the platform overview. */
export const adminKeys = {
  overview: ['admin', 'overview'] as const,
  subscriptions: (orgId: string | null) => ['admin', 'subscriptions', orgId] as const,
  entitlements: (orgId: string | null) => ['admin', 'entitlements', orgId] as const,
  subscriptionsAll: ['admin', 'subscriptions'] as const,
  entitlementsAll: ['admin', 'entitlements'] as const,
}

export function useAdminOverview() {
  return useApiQuery<AdminOverview>(adminKeys.overview, '/admin/overview')
}

/** Without an org the server shows the first investor org (by name). */
export function useSubscriptions(orgId: string | null) {
  return useApiQuery<SubscriptionOverview>(adminKeys.subscriptions(orgId), '/admin/subscriptions', {
    query: { org_id: orgId ?? undefined },
    placeholderData: (previous) => previous,
  })
}

export function useEntitlements(orgId: string | null) {
  return useApiQuery<EntitlementOverview>(adminKeys.entitlements(orgId), '/admin/entitlements', {
    query: { org_id: orgId ?? undefined },
    placeholderData: (previous) => previous,
  })
}

const subscriptionInvalidate = [adminKeys.subscriptionsAll, adminKeys.overview]
const entitlementInvalidate = [adminKeys.entitlementsAll, adminKeys.overview]

export function useGrantSubscription() {
  return useApiMutation(
    (vars: { org_id: string; engagement_id: string; reason: string }, { requestId }) =>
      api.post<{ subscription?: GrantRecord; entitlement?: GrantRecord }>(
        '/admin/subscriptions',
        vars,
        { requestId },
      ),
    { invalidate: subscriptionInvalidate },
  )
}

export function useRevokeSubscription() {
  return useApiMutation(
    (vars: { id: string; revision: number; reason: string }, { requestId }) =>
      api.post<{ subscription?: GrantRecord; entitlement?: GrantRecord }>(
        `/admin/subscriptions/${encodeURIComponent(vars.id)}/revoke`,
        { revision: vars.revision, reason: vars.reason },
        { requestId },
      ),
    { invalidate: subscriptionInvalidate },
  )
}

export function useAddRestriction() {
  return useApiMutation(
    (
      vars: { org_id: string; user_id: string; engagement_id: string; reason: string },
      { requestId },
    ) => api.post<Restriction>('/admin/subscriptions/restrictions', vars, { requestId }),
    { invalidate: subscriptionInvalidate },
  )
}

export function useLiftRestriction() {
  return useApiMutation(
    (vars: { id: string; reason: string }, { requestId }) =>
      api.post<{ id: string; lifted_at: string }>(
        `/admin/subscriptions/restrictions/${encodeURIComponent(vars.id)}/lift`,
        { reason: vars.reason },
        { requestId },
      ),
    { invalidate: subscriptionInvalidate },
  )
}

export function useGrantEntitlement() {
  return useApiMutation(
    (vars: { org_id: string; dataset: string; reason: string }, { requestId }) =>
      api.post<{ subscription?: GrantRecord; entitlement?: GrantRecord }>(
        '/admin/entitlements',
        vars,
        { requestId },
      ),
    { invalidate: entitlementInvalidate },
  )
}

export function useRevokeEntitlement() {
  return useApiMutation(
    (vars: { id: string; revision: number; reason: string }, { requestId }) =>
      api.post<{ subscription?: GrantRecord; entitlement?: GrantRecord }>(
        `/admin/entitlements/${encodeURIComponent(vars.id)}/revoke`,
        { revision: vars.revision, reason: vars.reason },
        { requestId },
      ),
    { invalidate: entitlementInvalidate },
  )
}
